import { log, PushProcessingNotification, styles } from "@repo/helpers";
import { Button, ButtonTypes, Card } from "@repo/ui";
import { LockIcon, UnlockIcon } from "lucide-react";
import { FC, useCallback, useMemo, useState } from "react";

import { useOnboardingSessionsApi } from "../../api/onboardingSessionsApi";
import { useRepQueries } from "../../api/repApi";
import { useRepSegmentsApi } from "../../api/repSegmentsApi";
import { useRepContext } from "../../contexts/editableItemContext";
import style from "./style.module.scss";
import { useSegmentWizard } from "./useSegmentWizard";

interface Props {
	stateId: number;
}

export const Available: FC<Props> = ({ stateId }) => {
	const { repId } = useRepContext();
	const { openSegmentWizard } = useSegmentWizard();
	const { possibleStatesQuery, onboardingRequirementsQuery } = useOnboardingSessionsApi();
	const { useRepSegmentsQuery, addRepresentativeSegment } = useRepSegmentsApi(repId);
	const { clearQuery: clearRepSegmentsQuery } = useRepSegmentsQuery();
	const { useStatusHistoryQuery } = useRepQueries();
	const { clearQuery: clearStatusHistoryQuery } = useStatusHistoryQuery();

	const [addingSegment, setAddingSegment] = useState(false);

	const state = useMemo(
		() => possibleStatesQuery.data?.find((state) => state.stateId === stateId),
		[possibleStatesQuery.data, stateId],
	);
	const title = state?.name;

	const addSegment = useCallback(
		async function () {
			const toast = PushProcessingNotification(`Enabling '${title}' Segment...`);
			try {
				setAddingSegment(true);
				const newId = await addRepresentativeSegment(stateId);
				await possibleStatesQuery.clearQuery();
				await clearRepSegmentsQuery();
				await onboardingRequirementsQuery.clearQuery();
				await clearStatusHistoryQuery();

				toast.success(`Enabled '${title}' Segment!`);
				openSegmentWizard(newId, title || "");
			} catch (e) {
				log(e, "error");
				toast.error(`Failed to enable '${title}' Segment`);
			} finally {
				setAddingSegment(false);
			}
		},
		[
			title,
			addRepresentativeSegment,
			stateId,
			possibleStatesQuery,
			clearRepSegmentsQuery,
			onboardingRequirementsQuery,
			clearStatusHistoryQuery,
			openSegmentWizard,
		],
	);

	return (
		<Card>
			<div className={style.flexBetween}>
				<div className={style.flexRow}>
					<h2 className={style.cardTitle}>{title}</h2>
					<LockIcon className={styles(style.textMuted, style.lockIcon)} />
				</div>
				<Button
					types={[ButtonTypes.Outline]}
					onClick={addSegment}
					loading={addingSegment}
					disabled={addingSegment}
				>
					{!addingSegment && (
						<span className={style.flexRowCenter}>
							<UnlockIcon className={style.lockIcon} />
							Enable
						</span>
					)}
				</Button>
			</div>
		</Card>
	);
};
